/**
 * LocalCatalogSource — ICatalogSource backed by local filesystem discovery.
 *
 * Walks local repos via `discoverAssetsInTree`, computes SHA256
 * over the deterministic file filter, and caches AssetDefinition rows in
 * the LocalIndex SQLite.
 *
 * Used by the CLI for local-only workflows. The platform uses the remote
 * catalog source (Phase 2).
 *
 * @docLink packages/library/concepts#local-catalog-source
 */

import { parseAssetRef, resolveSourcePublisher } from "@skaile/workspaces/core";
import {
  type DiscoveryResult,
  discoverAssetsInTree,
  isManifestMode,
  readManifestSourceConfig,
  type SourceConfig,
} from "@skaile/workspaces/discovery";
import type {
  CatalogAsset,
  CatalogAssetFilter,
  CatalogSourceInfo,
  IAssetKindRegistry,
  ICatalogSource,
} from "@skaile/workspaces/plugins";
import type { AssetDefinition, AssetFilter } from "../library.js";
import type { LocalIndex } from "./library.js";

// ---------------------------------------------------------------------------
// Error formatting
// ---------------------------------------------------------------------------

/**
 * Render an error plus its `cause` chain into one human-readable line.
 *
 * Driver errors (e.g. libsql) stringify to just the failing SQL — the actual
 * SQLITE reason (`no such table`, a CHECK / NOT NULL violation, …) lives on
 * `err.cause`. Surfacing the chain turns an opaque `Failed query: <SQL>` into an
 * actionable diagnostic. Repeated messages are deduped and cycles are guarded.
 *
 * @param err - The thrown value (any type).
 * @returns The message with each distinct cause message appended after `: `.
 */
export function describeSyncError(err: unknown): string {
  if (!(err instanceof Error)) return String(err);
  const parts: string[] = [err.message];
  const seen = new Set<unknown>([err]);
  let cause: unknown = (err as { cause?: unknown }).cause;
  while (cause && !seen.has(cause)) {
    seen.add(cause);
    const msg = cause instanceof Error ? cause.message : String(cause);
    if (msg && !parts.some((p) => p.includes(msg))) parts.push(msg);
    cause = (cause as { cause?: unknown }).cause;
  }
  return parts.join(": ");
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Optional constructor opts for {@link LocalCatalogSource}.
 *
 * @docLink packages/library/concepts#local-catalog-source
 */
export interface LocalCatalogSourceOptions {
  /**
   * Absolute path to a sidecar directory carrying a local-overlay manifest
   * (`~/.skaile/sidecars/<slug>/`). When set, discovery merges the sidecar's
   * `skaile.manifest.yaml` on top of the in-repo config before dispatch.
   *
   * Typically populated by `openLibrary()`-based callers reading
   * `source.sidecarPath` from the registered source row.
   */
  sidecarPath?: string;
  /**
   * Absolute path to a curated overlay manifest FILE — the local store's flat
   * `~/.skaile/store/manifests/<slug>.yaml`. When set, discovery merges this
   * file's `skaile.manifest.yaml`-shaped config on top of the in-repo config
   * (the store keeps a flat `<slug>.yaml`, not a `<dir>/skaile.manifest.yaml`,
   * so it can't go through `sidecarPath`). Takes precedence over `sidecarPath`.
   */
  sidecarManifestFile?: string;
}

// ---------------------------------------------------------------------------
// LocalCatalogSource
// ---------------------------------------------------------------------------

/**
 * Local-filesystem implementation of `ICatalogSource` (from `@skaile/plugins`).
 *
 * Backed by `discoverAssetsInTree` from `@skaile/discovery`. Upserts discovered
 * assets into the `LocalIndex` cache via `upsertAssetDef()`. Preset-kind assets
 * are skipped (handled by the Preset subsystem, not stored as AssetDefinitions).
 *
 * @docLink packages/library/concepts#local-catalog-source
 */
export class LocalCatalogSource implements ICatalogSource {
  readonly id: string;
  private readonly sidecarPath?: string;
  private readonly sidecarManifestFile?: string;

  constructor(
    private readonly library: LocalIndex,
    private readonly sourceId: string,
    private readonly rootPath: string,
    private readonly registry?: IAssetKindRegistry,
    options?: LocalCatalogSourceOptions,
  ) {
    this.id = `local:${sourceId}`;
    this.sidecarPath = options?.sidecarPath;
    this.sidecarManifestFile = options?.sidecarManifestFile;
  }

  async resolve(ref: string): Promise<CatalogAsset | null> {
    const def = await this.library.getAssetDef(ref);
    if (!def || def.libraryId !== this.sourceId) return null;
    return defToCatalogAsset(def);
  }

  async listAssets(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const libraryFilter: AssetFilter = { libraryId: this.sourceId };
    if (filter?.kind) libraryFilter.kind = filter.kind as AssetDefinition["kind"];
    if (filter?.publisher) libraryFilter.publisher = filter.publisher;
    if (filter?.prefix) libraryFilter.prefix = filter.prefix;

    const defs = await this.library.listAssetDefs(libraryFilter);
    return defs.map(defToCatalogAsset);
  }

  async listVersions(ref: string): Promise<string[]> {
    const parsed = parseAssetRef(ref);
    // Filter to this source + kind; match name/publisher in-memory since stored
    // publishers carry the `@` scope sigil that `parseAssetRef` strips.
    const defs = await this.library.listAssetDefs({
      libraryId: this.sourceId,
      kind: parsed.kind as AssetDefinition["kind"],
    });
    const wantPublisher = parsed.publisher?.replace(/^@/, "");
    return defs
      .filter(
        (d) =>
          d.name === parsed.name &&
          (wantPublisher === undefined || d.publisher.replace(/^@/, "") === wantPublisher),
      )
      .map((d) => d.version);
  }

  async fetchTarball(_ref: string, _sha256: string): Promise<Uint8Array> {
    throw new Error(
      "LocalCatalogSource does not support tarball delivery. Assets are available on disk.",
    );
  }

  async listSources(): Promise<CatalogSourceInfo[]> {
    return [{ id: this.sourceId, type: "local", url: this.rootPath }];
  }

  /**
   * Sync: discover assets in the source directory and cache them in the library.
   *
   * Runs the discovery pipeline (glob -> parse -> filter -> hash -> requires)
   * and upserts each discovered asset into the LocalIndex cache.
   *
   * @param syncOpts - When `includeDev` is set, assets under the source
   *   config's `dev_paths` (e.g. `ai-assets-dev/`) are discovered and cached
   *   too. Defaults to excluding them (a normal run).
   * @returns Discovery statistics, fatal `errors`, and non-fatal `advisories`
   *   (missing sha256, unknown kind) — the latter never signal a failed index.
   */
  async sync(syncOpts?: { includeDev?: boolean }): Promise<{
    assetsFound: number;
    assetsUpdated: number;
    errors: string[];
    advisories: string[];
  }> {
    // Overlay resolution: discoverAssetsInTree merges the in-repo base manifest
    // with either a curated store manifest FILE (`sidecarManifestFile`, the flat
    // `~/.skaile/store/manifests/<slug>.yaml`) or a sidecar DIR (`sidecarPath`,
    // legacy `<dir>/skaile.manifest.yaml`). The file overlay wins when both set.
    // The curated overlay declares its own publisher + asset inventory, so we
    // don't run `alignPublisher` (which is only for a manifest-less source).
    const opts: Parameters<typeof discoverAssetsInTree>[1] = {
      registry: this.registry,
      sidecarManifestFile: this.sidecarManifestFile,
      sidecarPath: this.sidecarPath,
      includeDev: syncOpts?.includeDev ?? false,
    };
    if (!this.sidecarPath && !this.sidecarManifestFile) {
      // No overlay: load the base manifest eagerly. When it declares no
      // publisher, inject the walker's publisher (manifest publisher ?? GitHub
      // org) so the index keys assets identically to `resolveAll` — without it a
      // manifest-less source indexes under the first path segment (`@skills`)
      // while resolution uses the org (`@acme`), splitting the same asset.
      opts.sourceConfig = this.alignPublisher(readManifestSourceConfig(this.rootPath));
    }
    // Sidecar-backed sources skip the alignment above and can still mis-key a
    // manifest-less source under a path segment; the full fix is a deferred follow-up.
    const result: DiscoveryResult = discoverAssetsInTree(this.rootPath, opts);

    let assetsUpdated = 0;
    const errors: string[] = [];

    // Body-link soft deps live on the cross-asset graph (a post-walk pass), not
    // in `asset.requires` (typed manifest edges). Pre-group by `from` once so the
    // per-asset lookup is O(1) instead of re-scanning every edge.
    const softDepsByRef = new Map<string, string[]>();
    for (const e of result.graph.edges) {
      if (e.field !== "body-link") continue;
      const list = softDepsByRef.get(e.from);
      if (list) list.push(e.to);
      else softDepsByRef.set(e.from, [e.to]);
    }

    for (const asset of result.assets) {
      // Preset is a composition entity, not an asset kind — skip library upsert.
      // Presets are still discovered (for the requires graph) but handled by the
      // Preset system in Task 1.6, not stored as AssetDefinitions.
      if (asset.kind === "preset") continue;

      const softDeps = softDepsByRef.get(asset.ref) ?? [];

      try {
        await this.library.upsertAssetDef({
          id: asset.ref,
          kind: asset.kind as AssetDefinition["kind"],
          publisher: asset.publisher,
          name: asset.name,
          version: asset.version,
          sha256: asset.sha256,
          description:
            typeof asset.manifest.description === "string" ? asset.manifest.description : undefined,
          license: typeof asset.manifest.license === "string" ? asset.manifest.license : undefined,
          category:
            typeof asset.manifest.category === "string" ? asset.manifest.category : undefined,
          manifest: asset.manifest,
          libraryId: this.sourceId,
          softDeps,
          cachedAt: new Date(),
          updatedAt: new Date(),
        });
        assetsUpdated++;
      } catch (err) {
        errors.push(`${asset.ref}: ${describeSyncError(err)}`);
      }
    }

    for (const err of result.errors) {
      errors.push(`${err.path}: ${err.error}`);
    }

    // Non-fatal advisories (missing sha256, unknown kind) — surfaced separately
    // so the CLI can print them without counting the source as failed-to-index.
    // A curated overlay with no pinned hashes yields one `missing_sha256` warning
    // per asset; collapse those into a single summary line (kept last) instead of
    // printing dozens of near-identical lines. Everything else prints as before.
    const advisories: string[] = [];
    let missingSha256Count = 0;
    for (const w of result.warnings) {
      if (w.code === "missing_sha256") {
        missingSha256Count++;
        continue;
      }
      advisories.push(`${w.path}: ${w.warning}`);
    }
    if (missingSha256Count > 0) {
      advisories.push(
        `${missingSha256Count} asset(s): sha256 not pinned — auto-computed locally (pin only for store publish)`,
      );
    }

    // Prune stale rows: a sync is authoritative for this source, so a def
    // re-keyed under a new publisher leaves no orphan behind. Skip on an empty
    // discovery — `discoverAssetsInTree` swallows readdir errors and yields `[]`
    // for a missing/unreadable root, which would otherwise silently wipe the
    // source. Clearing a genuinely-empty source is `removeSource`'s job.
    const keepRefs = result.assets.filter((a) => a.kind !== "preset").map((a) => a.ref);
    if (keepRefs.length > 0) await this.library.pruneAssetDefs(this.sourceId, keepRefs);

    return { assetsFound: result.assets.length, assetsUpdated, errors, advisories };
  }

  /**
   * When the loaded source config declares no publisher (no manifest, or a
   * manifest without `publisher`), substitute the canonical publisher derived
   * the same way the provenance walker derives it (`resolveSourcePublisher`).
   * Leaves manifest-mode (`assets:` inventory) and explicitly-published configs
   * untouched, and is a no-op outside a git checkout (`resolveSourcePublisher`
   * returns `undefined`) — preserving the legacy first-path-segment fallback.
   */
  private alignPublisher(config: SourceConfig | undefined): SourceConfig | undefined {
    // `<domain>` is the sentinel manifestToSourceConfig emits when no publisher
    // is declared; its absence means the manifest declared one explicitly.
    if (config && (isManifestMode(config) || !config.publisher_default.includes("<domain>"))) {
      return config;
    }
    const publisher = resolveSourcePublisher(this.rootPath);
    if (!publisher) return config;
    // Spread the existing config and override ONLY publisher_default so optional
    // fields (`sync`, and any future ones) are forwarded, not silently dropped.
    const base: SourceConfig = config ?? {
      version: 1,
      publisher_default: `@${publisher}`,
      default_ref: "main",
      publisher_overrides: [],
      dev_paths: [],
    };
    return { ...base, publisher_default: `@${publisher}` };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defToCatalogAsset(def: AssetDefinition): CatalogAsset {
  return {
    id: def.id,
    kind: def.kind,
    publisher: def.publisher,
    name: def.name,
    version: def.version,
    sha256: def.sha256,
    description: def.description,
    license: def.license,
    category: def.category,
    manifest: def.manifest,
  };
}
